import { useEffect, useState, type FormEvent } from "react";
import { useAuth } from "./AuthContext";
import { Badge, Button, Callout, Field, Input, Modal } from "../shared/ui/Primitives";
import { useToast } from "../shared/ui/Toast";

interface BitrixAuthPayload {
  domain: string;
  member_id: string;
  access_token: string;
  refresh_token: string;
  expires_in: number;
}

function value(record: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const candidate = record[key];
    if (typeof candidate === "string" && candidate) return candidate;
  }
  return "";
}

function readBitrixAuth(): Promise<BitrixAuthPayload> {
  return new Promise((resolve, reject) => {
    if (!window.BX24) {
      reject(new Error("Откройте приложение внутри Bitrix24, чтобы выполнить привязку."));
      return;
    }
    window.BX24.init(() => {
      const auth = window.BX24?.getAuth() ?? {};
      const accessToken = value(auth, "access_token", "AUTH_ID");
      const refreshToken = value(auth, "refresh_token", "REFRESH_ID");
      const domain = value(auth, "domain", "DOMAIN");
      const memberId = value(auth, "member_id", "MEMBER_ID");
      const rawExpires = auth.expires_in ?? auth.AUTH_EXPIRES ?? 3600;
      const expiresIn = Number(rawExpires);
      if (!accessToken || !refreshToken || !domain || !memberId || !Number.isFinite(expiresIn)) {
        reject(new Error("Bitrix24 не передал полный набор данных для безопасной привязки."));
        return;
      }
      resolve({
        domain,
        member_id: memberId,
        access_token: accessToken,
        refresh_token: refreshToken,
        expires_in: expiresIn,
      });
    });
  });
}

export function AccountModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { api, user, session, setPassword, retry } = useAuth();
  const { showToast } = useToast();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [saving, setSaving] = useState(false);
  const [linking, setLinking] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (!open) return;
    setCurrentPassword("");
    setNewPassword("");
    setConfirmation("");
    setFormError("");
  }, [open]);

  if (!user || !session) return null;
  const userHasPassword = user.has_password;

  async function submitPassword(event: FormEvent) {
    event.preventDefault();
    if (newPassword.length < 12) {
      setFormError("Новый пароль должен содержать не менее 12 символов.");
      return;
    }
    if (newPassword !== confirmation) {
      setFormError("Пароли не совпадают.");
      return;
    }
    setSaving(true);
    setFormError("");
    try {
      await setPassword(newPassword, currentPassword || undefined);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmation("");
      showToast(userHasPassword ? "Пароль изменён" : "Вход по паролю включён", "success");
    } catch (error) {
      setFormError(error instanceof Error ? error.message : "Не удалось сохранить пароль");
    } finally {
      setSaving(false);
    }
  }

  async function linkBitrix() {
    setLinking(true);
    try {
      await api.linkBitrix(await readBitrixAuth());
      await retry();
      showToast("Учётная запись Bitrix24 привязана", "success");
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Не удалось привязать Bitrix24", "danger");
    } finally {
      setLinking(false);
    }
  }

  return (
    <Modal open={open} title="Профиль и способы входа" onClose={onClose}>
      <div className="account-settings">
        <section>
          <div className="account-identity">
            <span className="user-avatar user-avatar--large">
              {(user.first_name[0] || user.last_name[0] || "П").toUpperCase()}
            </span>
            <div>
              <h3>{`${user.first_name} ${user.last_name}`.trim() || user.email}</h3>
              <p>{user.email || "Почта не указана"}</p>
            </div>
          </div>
          <div className="auth-methods">
            <Badge tone={session.bitrix.linked ? "success" : "neutral"}>
              Bitrix24: {session.bitrix.linked ? "привязан" : "не привязан"}
            </Badge>
            <Badge tone={user.has_password ? "success" : "neutral"}>
              Пароль: {user.has_password ? "настроен" : "не настроен"}
            </Badge>
            <Badge tone="info">
              Текущий вход: {session.auth_method === "bitrix" ? "Bitrix24" : "почта"}
            </Badge>
          </div>
          {!session.bitrix.linked ? (
            <Callout tone="info" title="Привязать Bitrix24">
              Подтвердите текущего пользователя портала. Приложение проверит его через
              <code> user.current</code> и сохранит связь с этим же внутренним аккаунтом.
              <div className="callout-action">
                <Button size="small" variant="secondary" onClick={() => void linkBitrix()} disabled={linking}>
                  {linking ? "Проверяем…" : "Привязать текущий Bitrix24"}
                </Button>
              </div>
            </Callout>
          ) : null}
        </section>

        <form className="password-form" onSubmit={submitPassword}>
          <div>
            <h3>{user.has_password ? "Изменить пароль" : "Включить вход по почте"}</h3>
            <p>После сохранения все остальные активные сессии будут отозваны.</p>
          </div>
          {user.has_password && session.auth_method === "password" ? (
            <Field label="Текущий пароль">
              <Input
                type="password"
                autoComplete="current-password"
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
                required
              />
            </Field>
          ) : null}
          <Field label="Новый пароль" hint="Не менее 12 символов.">
            <Input
              type="password"
              autoComplete="new-password"
              minLength={12}
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              required
            />
          </Field>
          <Field label="Повторите пароль" error={formError || undefined}>
            <Input
              type="password"
              autoComplete="new-password"
              minLength={12}
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
              required
            />
          </Field>
          <Button type="submit" disabled={saving || !newPassword || !confirmation}>
            {saving ? "Сохраняем…" : user.has_password ? "Изменить пароль" : "Включить вход"}
          </Button>
        </form>
      </div>
    </Modal>
  );
}
